<template>
  <div class="kt-portlet mt-5 mt-md-3 mt-lg-0">
    <div
      class="kt-portlet__head kt-portlet__head kt-portlet__head--lg kt-portlet__head--noborder kt-portlet__head--break-sm flex-wrap"
    >
      <div class="kt-portlet__head-label pt-3 pt-md-0">
        <span class="kt-portlet__head-icon">
          <i class="fal fa-list"></i>
        </span>
        <h3 class="kt-portlet__head-title">Activity Log</h3>
      </div>
      <div class="kt-portlet__head-toolbar">
        <div class="kt-portlet__head-group d-flex flex-wrap justify-center" />
      </div>
    </div>
    <div class="kt-portlet__body">
      <div class="d-flex">
        <div style="width: 230px">
          <v-select
            v-model="filter.type"
            :items="table.types"
            label="Select Type"
            solo
            @change="typeChange"
            dense
            :loading="loading"
            :disabled="loading"
          />

          <v-menu
            :close-on-content-click="false"
            :nudge-right="40"
            transition="scale-transition"
            offset-y
            min-width="auto"
            v-model="customDateRangeMenu"
            ref="customDateRangeMenu"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="customDateRangeText"
                label="Select Custom Date Ranges"
                readonly
                v-bind="attrs"
                v-on="on"
                ref="customRange"
                clearable
                clear-icon="fal fa-times-circle"
                v-show="filter.type === 'custom'"
              />
            </template>
            <v-date-picker
              v-model="filter.dateRange"
              range
              no-title
              :max="maxDateRange"
              @change="dateSelected"
            >
              <v-spacer></v-spacer>
              <v-btn
                text
                color="primary"
                @click="customDateRangeMenu = false"
                :disabled="loading"
              >
                Cancel
              </v-btn>
              <v-btn
                text
                color="primary"
                @click="
                  getActivityLogs();
                  $refs.customDateRangeMenu.save(date);
                "
                :loading="loading"
                :disabled="loading"
              >
                OK
              </v-btn>
            </v-date-picker>
          </v-menu>
        </div>

        <div style="width: 30%" class="ml-5">
          <v-combobox
            v-model="filter.categories"
            :items="categories"
            placeholder="Filter by Type"
            clearable
            small-chips
            dense
            solo
            multiple
            :loading="loading"
            :disabled="loading"
            hide-details
          />
        </div>

        <div style="width: 30%" class="ml-5">
          <v-combobox
            v-model="filter.users"
            :items="usersOptions"
            placeholder="Filter by Users"
            clearable
            small-chips
            dense
            solo
            multiple
            :loading="loading"
            :disabled="loading"
            hide-details
          />
        </div>
      </div>

      <v-skeleton-loader
        class="mx-auto"
        type="table"
        width="100%"
        v-if="loading"
      />

      <v-data-table
        v-else
        :headers="table.header"
        :items="filteredRows"
        :items-per-page="table.rowsPerPage"
        class="elevation-1"
        sort-by="created_at"
        :sort-desc="false"
      />
    </div>
  </div>
</template>

<script>
import { map as _map, uniq as _uniq, filter as _filter } from 'lodash-es';
import moment from 'moment';
import EventBus from '@/plugins/eventBus';

function defaultFilter() {
  return {
    type: '90days',
    dateRange: [moment().format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')],
    categories: [],
    users: [],
  };
}

export default {
  name: 'ActivityLog',

  data() {
    return {
      table: {
        header: [
          { text: 'Name', value: 'name' },
          { text: 'Activity Type', value: 'type' },
          { text: 'User', value: 'user' },
          { text: 'Date', value: 'created_at' },
        ],
        rows: [],

        rowsPerPage: 15,

        types: ['90days', '60days', '30days', '7days', 'custom'],
      },

      filter: defaultFilter(),

      loading: true,

      customDateRangeMenu: false,

      maxDateRange: moment().endOf('day').format('YYYY-MM-DD'),

      users: [],
    };
  },

  created() {
    this.getActivityLogs();

    EventBus.$on('reset', () => {
      this.resetAllFilters();
    });
  },

  computed: {
    categories() {
      const data = _map(this.table.rows, (item) => item.type);
      return _uniq(data);
    },

    customDateRangeText() {
      const date = this.filter.dateRange;
      const dateFromText =
        typeof date[0] !== 'undefined'
          ? moment(date[0]).format('MM/DD/YYYY')
          : '';
      const dateToText =
        typeof date[1] !== 'undefined'
          ? moment(date[1]).format('MM/DD/YYYY')
          : '';

      return `${dateFromText} ~ ${dateToText}`;
    },

    filteredRows() {
      if (this.filter.categories.length < 1 && this.filter.users.length < 1) {
        return this.table.rows;
      }

      return _filter(this.table.rows, (item) => {
        if (this.filter.categories.length > 0 && this.filter.users.length < 1) {
          return this.filter.categories.includes(item.type);
        }

        if (this.filter.users.length > 0 && this.filter.categories.length < 1) {
          return this.filter.users.includes(item.user);
        }

        return (
          this.filter.categories.includes(item.type) &&
          this.filter.users.includes(item.user)
        );
      });
    },

    usersOptions() {
      return _map(this.users, (item) => item.name);
    },
  },

  methods: {
    getActivityLogs() {
      this.loading = true;
      this.$http
        .post('/client/reports/lms/activity', {
          type: this.filter.type,
          dateRange: this.filter.dateRange,
        })
        .then(({ data }) => {
          this.table.rows = data.rows;

          if (this.users.length < 1) {
            this.users = data.users;
          }

          this.loading = false;
          this.customDateRangeMenu = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },

    typeChange() {
      if (this.filter.type === 'custom') {
        this.$nextTick(() => (this.customDateRangeMenu = true));
        return;
      }

      this.getActivityLogs();
    },

    dateSelected(value) {
      const dateFrom = moment(value[0]);
      const dateTo = moment(value[1]);
      const dateFormat = 'YYYY-MM-DD';

      // check if the selected "date from" is greater than
      // the "date to", if yes
      // then swap it
      if (dateFrom.isAfter(dateTo)) {
        this.filter.dateRange = [
          dateTo.format(dateFormat),
          dateFrom.format(dateFormat),
        ];
      }
    },

    resetAllFilters() {
      this.filter = defaultFilter();
    },
  },
};
</script>

<style scoped></style>
